import { Theme } from '@monorepo/relayably/theme'
import { LinearGradient } from 'expo-linear-gradient'
import { Bot, Home, MessageSquareMore, PanelLeftClose, PanelLeftOpen, Settings, Sparkles } from 'lucide-react-native'
import React from 'react'
import { Image, Pressable, SafeAreaView, StyleSheet, Text, useWindowDimensions, View } from 'react-native'
import { DashboardRoute, DashboardTab } from '../types'

const logo = require('../../../../assets/relayably/logo-256.png')

const tabs: Array<{ key: DashboardTab; label: string; icon: React.ComponentType<any> }> = [
  { key: 'home', label: 'Home', icon: Home },
  { key: 'talk', label: 'Talk', icon: MessageSquareMore },
  { key: 'chat', label: 'Chat', icon: Sparkles },
  { key: 'agents', label: 'Agents', icon: Bot },
  { key: 'settings', label: 'Settings', icon: Settings },
]

const hexToRgba = (hex: string, alpha: number) => {
  if (!hex.startsWith('#')) {
    return hex
  }

  const normalized = hex.slice(1)
  const value =
    normalized.length === 3
      ? normalized
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : normalized

  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const DashboardShell = ({
  theme,
  route,
  title,
  subtitle,
  collapsed,
  onToggleCollapsed,
  onBack,
  onSelectTab,
  disabledTabs,
  sidebarSections,
  banner,
  children,
}: {
  theme: Theme
  route: DashboardRoute
  title: string
  subtitle: string
  collapsed: boolean
  onToggleCollapsed: () => void
  onBack?: () => void
  onSelectTab: (tab: DashboardTab) => void
  disabledTabs?: DashboardTab[]
  sidebarSections?: Array<{
    key: string
    label: string
    active: boolean
    onPress: () => void
  }>
  banner?: React.ReactNode
  children: React.ReactNode
}) => {
  const { width } = useWindowDimensions()
  const isCompact = width < 760
  const activeTab = route.tab
  const styles = createStyles(theme, collapsed, isCompact)

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.shell}>
        {!isCompact ? (
          <LinearGradient colors={[hexToRgba(theme.colors.surface, 0.98), hexToRgba(theme.colors.card, 0.94)]} style={styles.sidebar}>
            <View style={styles.brandRow}>
              {!collapsed ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                  <Image source={logo} style={styles.collapsedLogo} />
                  <Text style={styles.brandTitle}>Relayably</Text>
                </View>
              ) : (
                <Pressable onPress={onToggleCollapsed}>
                  <Image source={logo} style={styles.collapsedLogo} />
                </Pressable>
              )}
              <Pressable onPress={onToggleCollapsed} style={styles.menuButton}>
                {!collapsed && <PanelLeftClose color={theme.colors.text} size={18} />}
              </Pressable>
            </View>

            <View style={styles.navStack}>
              {tabs
                .filter(tab => tab.key !== 'chat')
                .map(tab => {
                  const Icon = tab.icon
                  const active = tab.key === activeTab
                  const disabled = disabledTabs?.includes(tab.key) ?? false
                  return (
                    <View key={tab.key} style={styles.navSection}>
                      <Pressable disabled={disabled} onPress={() => onSelectTab(tab.key)} style={[styles.navItem, active && styles.navItemActive, disabled ? styles.disabledItem : undefined]}>
                        <Icon color={active ? theme.colors.primary : theme.colors.inactive} size={20} />
                        {!collapsed ? <Text style={[styles.navLabel, active && styles.navLabelActive]}>{tab.label}</Text> : null}
                      </Pressable>
                      {!collapsed && active && sidebarSections?.length ? (
                        <View style={styles.subnavGroup}>
                          {sidebarSections.map(section => (
                            <Pressable
                              key={section.key}
                              onPress={section.onPress}
                              style={[styles.subnavItem, section.active ? styles.subnavItemActive : undefined]}
                            >
                              <Text style={[styles.subnavLabel, section.active ? styles.subnavLabelActive : undefined]}>
                                {section.label}
                              </Text>
                            </Pressable>
                          ))}
                        </View>
                      ) : null}
                    </View>
                  )
                })}
            </View>

            <Pressable disabled={disabledTabs?.includes('chat')} onPress={() => onSelectTab('chat')} style={[styles.aiBubble, disabledTabs?.includes('chat') ? styles.disabledItem : undefined]}>
              <Sparkles color={theme.colors.staticWhite} size={22} />
              {!collapsed ? <Text style={styles.aiBubbleLabel}>Chat</Text> : null}
            </Pressable>
          </LinearGradient>
        ) : null}

        <View style={styles.main}>
          <LinearGradient colors={[hexToRgba(theme.colors.background, 1), hexToRgba(theme.colors.surface, 0.98)]} style={styles.header}>
            <View style={styles.headerTitleGroup}>
              {route.kind !== 'tab' ? (
                <Pressable onPress={onBack} style={styles.backButton}>
                  <Text style={styles.backText}>Back</Text>
                </Pressable>
              ) : null}
              <Text style={styles.headerTitle}>{title}</Text>
              <Text style={styles.headerSubtitle}>{subtitle}</Text>
            </View>
          </LinearGradient>

          {banner ?? null}
          <View style={styles.content}>{children}</View>
        </View>
      </View>

      {isCompact ? (
        <View style={styles.mobileTabs}>
          {tabs.map(tab => {
            const Icon = tab.icon
            const active = tab.key === activeTab
            const primary = tab.key === 'chat'
            const disabled = disabledTabs?.includes(tab.key) ?? false
            return (
              <Pressable
                key={tab.key}
                disabled={disabled}
                onPress={() => onSelectTab(tab.key)}
                style={[styles.mobileTab, primary && styles.mobileTabPrimary, active && !primary && styles.mobileTabActive, disabled ? styles.disabledItem : undefined]}
              >
                <Icon
                  color={primary ? theme.colors.staticWhite : active ? theme.colors.primary : theme.colors.inactive}
                  size={primary ? 24 : 20}
                />
                {!primary ? <Text style={[styles.mobileTabLabel, active && styles.mobileTabLabelActive]}>{tab.label}</Text> : null}
              </Pressable>
            )
          })}
        </View>
      ) : null}
    </SafeAreaView>
  )
}

const createStyles = (theme: Theme, collapsed: boolean, compact: boolean) =>
  StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    shell: {
      flex: 1,
      flexDirection: 'row',
    },
    sidebar: {
      width: collapsed ? 92 : 264,
      paddingHorizontal: 14,
      paddingTop: 18,
      paddingBottom: 24,
      borderRightWidth: 1,
      borderRightColor: hexToRgba(theme.colors.primary, 0.12),
      justifyContent: 'space-between',
    },
    brandRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginStart: 8,
      marginBottom: 24,
      gap: 12,
    },
    brandTitle: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 24,
      fontWeight: '700',
    },
    collapsedBrandMark: {
      width: 24,
      height: 38,
      borderRadius: 19,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: hexToRgba(theme.colors.primary, 0.08),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.14),
    },
    collapsedLogo: {
      width: 40,
      height: 40,
    },
    brandSubtitle: {
      color: theme.colors.inactive,
      fontSize: 12,
      marginTop: 4,
    },
    menuButton: {
      width: 34,
      height: 34,
      borderRadius: 17,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: hexToRgba(theme.colors.primary, 0.08),
    },
    navStack: {
      flex: 1,
      gap: 10,
    },
    navSection: {
      gap: 8,
    },
    subnavGroup: {
      marginLeft: 16,
      gap: 8,
      paddingLeft: 12,
      borderLeftWidth: 1,
      borderLeftColor: hexToRgba(theme.colors.primary, 0.14),
    },
    navItem: {
      minHeight: 52,
      borderRadius: 18,
      paddingHorizontal: collapsed ? 0 : 16,
      alignItems: 'center',
      justifyContent: collapsed ? 'center' : 'flex-start',
      flexDirection: 'row',
      gap: 12,
    },
    navItemActive: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.12),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.18),
    },
    disabledItem: {
      opacity: 0.35,
    },
    navLabel: {
      color: theme.colors.inactive,
      fontSize: 14,
      fontWeight: '600',
    },
    navLabelActive: {
      color: theme.colors.primary,
    },
    subnavItem: {
      minHeight: 38,
      borderRadius: 14,
      paddingHorizontal: 12,
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    subnavItemActive: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.12),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.18),
    },
    subnavLabel: {
      color: theme.colors.inactive,
      fontSize: 13,
      fontWeight: '600',
    },
    subnavLabelActive: {
      color: theme.colors.primary,
    },
    aiBubble: {
      minHeight: 54,
      borderRadius: 20,
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      gap: 10,
      paddingHorizontal: collapsed ? 0 : 18,
    },
    aiBubbleLabel: {
      color: theme.colors.staticWhite,
      fontSize: 14,
      fontWeight: '700',
    },
    main: {
      flex: 1,
    },
    header: {
      paddingHorizontal: compact ? 18 : 28,
      paddingTop: 16,
      paddingBottom: 18,
      borderBottomWidth: 1,
      borderBottomColor: hexToRgba(theme.colors.primary, 0.08),
    },
    headerTitleGroup: {
      gap: 6,
    },
    backButton: {
      alignSelf: 'flex-start',
    },
    backText: {
      color: theme.colors.primary,
      fontSize: 13,
      fontWeight: '700',
    },
    headerTitle: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: compact ? 28 : 34,
      lineHeight: compact ? 32 : 38,
    },
    headerSubtitle: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
      maxWidth: 640,
    },
    content: {
      flex: 1,
      paddingHorizontal: compact ? 16 : 24,
      paddingTop: 20,
      paddingBottom: compact ? 98 : 28,
    },
    mobileTabs: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      minHeight: 78,
      paddingBottom: 12,
      paddingTop: 10,
      paddingHorizontal: 12,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      backgroundColor: hexToRgba(theme.colors.surface, 0.98),
      borderTopWidth: 1,
      borderTopColor: hexToRgba(theme.colors.primary, 0.1),
    },
    mobileTab: {
      flex: 1,
      alignItems: 'center',
      gap: 6,
    },
    mobileTabPrimary: {
      maxWidth: 62,
      minHeight: 62,
      borderRadius: 31,
      backgroundColor: theme.colors.primary,
      justifyContent: 'center',
      marginTop: -28,
      alignSelf: 'center',
    },
    mobileTabActive: {
      transform: [{ translateY: -2 }],
    },
    mobileTabLabel: {
      color: theme.colors.inactive,
      fontSize: 10,
      fontWeight: '700',
    },
    mobileTabLabelActive: {
      color: theme.colors.primary,
    },
  })
